import { useState, useEffect } from 'react';

const useOfflineStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [offlineQueue, setOfflineQueue] = useState([]);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      console.log('🟢 Back online - processing queued messages');
      processOfflineQueue();
    };

    const handleOffline = () => {
      setIsOnline(false);
      console.log('🔴 Gone offline - messages will be queued');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Add message to offline queue
  const addToOfflineQueue = (message) => {
    setOfflineQueue(prev => [...prev, {
      ...message,
      timestamp: Date.now(),
      id: `offline_${Date.now()}_${Math.random()}`
    }]);
  };

  // Enhanced offline queue processing with Firestore sync
  const processOfflineQueue = async () => {
    if (offlineQueue.length === 0) return;

    console.log(`📤 Processing ${offlineQueue.length} queued messages`);
    
    let syncedCount = 0;
    let failedCount = 0;
    
    // Process each offline message
    for (const offlineMsg of offlineQueue) {
      try {
        // Import Firestore functions dynamically to avoid circular dependencies
        const { collection, addDoc, serverTimestamp } = await import('firebase/firestore');
        const { db } = await import('../firebase');
        
        // Send offline message to Firestore
        const messagesCol = collection(db, 'sellerbuyerconversations', offlineMsg.conversationId, 'messages');
        await addDoc(messagesCol, {
          from: offlineMsg.from,
          fromUid: offlineMsg.fromUid,
          text: offlineMsg.text,
          timestamp: serverTimestamp(),
          type: 'text',
          readBy: [offlineMsg.from],
          integrityHash: offlineMsg.integrityHash,
          wasOffline: true // Mark as originally sent offline
        });
        
        syncedCount++;
        console.log(`✅ Synced offline message: ${offlineMsg.text.substring(0, 30)}...`);
        
      } catch (error) {
        failedCount++;
        console.error('❌ Failed to sync offline message:', error);
      }
    }
    
    // Clear the queue after processing
    setOfflineQueue([]);
    
    // Log sync results
    if (syncedCount > 0) {
      console.log(`✅ Successfully synced ${syncedCount} offline messages`);
    }
    if (failedCount > 0) {
      console.log(`❌ Failed to sync ${failedCount} offline messages`);
    }
  };

  // Get offline status with more details
  const getOfflineStatus = () => {
    return {
      isOnline,
      offlineQueueCount: offlineQueue.length,
      lastSeen: isOnline ? null : new Date().toLocaleString(),
      canSendMessages: isOnline,
      canReceiveMessages: isOnline,
      canUploadFiles: isOnline
    };
  };

  return {
    isOnline,
    offlineQueue,
    addToOfflineQueue,
    processOfflineQueue,
    getOfflineStatus
  };
};

export default useOfflineStatus; 